import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import { isErrorResponse } from '../../utils/error-handlers';
import {
  DEFAULT_DELETE_CONFIG,
  DEFAULT_GET_CONFIG,
  DEFAULT_POST_CONFIG,
  DEFAULT_PUT_CONFIG,
} from '../../config/rest';

import {
  CLEAR_DISCOUNT_CODE,
  CLEAR_DISCOUNT_CODES,
  FLAG_DISCOUNT_CODE_ACTIVITY,
  FLAG_GETTING_DISCOUNT_CODES,
  GET_DISCOUNT_CODE,
  GET_DISCOUNT_CODES,
} from '../types';
import { DISCOUNT_CODE_PATH } from '../../config/paths';
import { DISCOUNT_CODE_ENDPOINT } from '../../config/endpoints';

const flagGettingDiscountCodes = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_DISCOUNT_CODES,
    payload: flag,
  });

const getDiscountCodes =
  async (tableFilters = null) =>
  async (dispatch) => {
    try {
      dispatch(flagGettingDiscountCodes(true));
      const query = tableFilters;
      const url = `${DISCOUNT_CODE_ENDPOINT}?${QueryString.stringify(query)}`;
      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });
      await isErrorResponse(response, null, dispatch);
      const pricingProfiles = await response.json();
      dispatch({ type: GET_DISCOUNT_CODES, payload: pricingProfiles });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagGettingDiscountCodes(false));
    }
  };

const clearDiscountCodes = () => (dispatch) =>
  dispatch({
    type: CLEAR_DISCOUNT_CODES,
  });

const clearDiscountCode = () => (dispatch) =>
  dispatch({
    type: CLEAR_DISCOUNT_CODE,
  });

const flagDiscountCodeActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_DISCOUNT_CODE_ACTIVITY,
    payload: flag,
  });

const postDiscountCode =
  async ({
    code,
    customerSet,
    description,
    discount,
    discountType,
    excludedDateRangeSet,
    excludedRouteSet,
    includedRouteSet,
    isValid,
    maxAmount,
    maxNumberOfUses,
    name,
    productType,
    serviceTypeId,
    validFrom,
    validUntil,
    includeAgencySet,
    includeUserSet,
    includeSecurityProfileSet,
    agencyContractTypeDiscountCodeSet,
    applyOnlyAfterDeparture,
    discountCodeTripRuleList,
    authorizerUserId,
    discountCodeCategoryId,
  }) =>
  async (dispatch) => {
    try {
      dispatch(flagDiscountCodeActivity(true));

      const payload = {
        code,
        customerSet,
        description,
        discount,
        discountType,
        excludedDateRangeSet,
        excludedRouteSet,
        includedRouteSet,
        isValid,
        maxAmount,
        maxNumberOfUses,
        name,
        productType,
        serviceTypeId,
        validFrom,
        validUntil,
        includeAgencySet,
        includeUserSet,
        includeSecurityProfileSet,
        agencyContractTypeDiscountCodeSet,
        applyOnlyAfterDeparture,
        discountCodeTripRuleList,
        authorizerUserId,
        discountCodeCategoryId,
      };

      const url = DISCOUNT_CODE_ENDPOINT;

      const response = await fetch(url, {
        ...DEFAULT_POST_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const discountCode = await response.json();

      dispatch(push(`${DISCOUNT_CODE_PATH}/${discountCode.id}`));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagDiscountCodeActivity(false));
    }
  };

const getDiscountCode =
  async ({ discountCodeId }) =>
  async (dispatch) => {
    try {
      dispatch(flagDiscountCodeActivity(true));

      const url = `${DISCOUNT_CODE_ENDPOINT}/${discountCodeId}`;

      const response = await fetch(url, { ...DEFAULT_GET_CONFIG });

      await isErrorResponse(response, null, dispatch);

      const pricingProfile = await response.json();

      dispatch({
        type: GET_DISCOUNT_CODE,
        payload: pricingProfile,
      });
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagDiscountCodeActivity(false));
    }
  };

const putDiscountCode =
  async (
    discountCodeId,
    {
      code,
      customerSet,
      description,
      discount,
      discountType,
      excludedDateRangeSet,
      excludedRouteSet,
      includedRouteSet,
      isValid,
      maxAmount,
      maxNumberOfUses,
      name,
      productType,
      serviceTypeId,
      validFrom,
      validUntil,
      numberOfUses,
      includeAgencySet,
      includeUserSet,
      includeSecurityProfileSet,
      agencyContractTypeDiscountCodeSet,
      applyOnlyAfterDeparture,
      discountCodeTripRuleList,
      authorizerUserId,
      discountCodeCategoryId,
    },
  ) =>
  async (dispatch) => {
    try {
      dispatch(flagDiscountCodeActivity(true));

      const payload = {
        id: discountCodeId,
        code,
        customerSet,
        description,
        discount,
        discountType,
        excludedDateRangeSet,
        excludedRouteSet,
        includedRouteSet,
        isValid,
        maxAmount,
        maxNumberOfUses,
        name,
        productType,
        serviceTypeId,
        validFrom,
        validUntil,
        numberOfUses,
        includeAgencySet,
        includeUserSet,
        includeSecurityProfileSet,
        agencyContractTypeDiscountCodeSet,
        applyOnlyAfterDeparture,
        discountCodeTripRuleList,
        authorizerUserId,
        discountCodeCategoryId,
      };

      const url = `${DISCOUNT_CODE_ENDPOINT}/${discountCodeId}`;

      const response = await fetch(url, {
        ...DEFAULT_PUT_CONFIG,
        body: JSON.stringify(payload),
      });

      await isErrorResponse(response, null, dispatch);

      const discountCode = await response.json();

      dispatch(push(`${DISCOUNT_CODE_PATH}/${discountCode.id}`));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagDiscountCodeActivity(false));
    }
  };

const deleteDiscountCode =
  async ({ discountCodeId }) =>
  async (dispatch) => {
    try {
      dispatch(flagDiscountCodeActivity(true));

      const url = `${DISCOUNT_CODE_ENDPOINT}/${discountCodeId}`;

      const response = await fetch(url, {
        ...DEFAULT_DELETE_CONFIG,
      });

      await isErrorResponse(response, null, dispatch);

      dispatch(push(DISCOUNT_CODE_PATH));
    } catch (error) {
      toastr.error('Error', error.message);
    } finally {
      dispatch(flagDiscountCodeActivity(false));
    }
  };

export {
  flagGettingDiscountCodes,
  getDiscountCodes,
  clearDiscountCodes,
  clearDiscountCode,
  postDiscountCode,
  getDiscountCode,
  putDiscountCode,
  deleteDiscountCode,
};
